import React, { useState, ChangeEvent, useEffect } from 'react';
import {
	TextField,
	Checkbox,
	FormControlLabel,
	Button,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	IconButton,
	SelectChangeEvent,
	Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { getAllCategory } from '../../services/api/Category';

interface FormData {
	name: string;
	description: string;
	category: string;
	price: string;
	quantity: string;
	bestSelling: boolean;
	trending: boolean;
	productImage: File[];
	weight: string;
	weightUnit: string;
}

const CreateProducts: React.FC = () => {
	const [formData, setFormData] = useState<FormData>({
		name: '',
		description: '',
		category: '',
		price: '',
		quantity: '',
		bestSelling: false,
		trending: false,
		productImage: [],
		weight: '',
		weightUnit: 'kg',
	});
	const [imagePreviews, setImagePreviews] = useState<string[]>([]);
	const [categories, setCategories] = useState<{ categoryName: string; productCount: number }[]>(
		[]
	);

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const data = await getAllCategory();
				setCategories(data?.data || []);
				console.log(data, 'data');
			} catch (error) {
				console.error('Failed to fetch categories:', error);
			}
		};
		fetchCategories();
	}, []);

	const handleChange = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
	) => {
		const { name, value } = e.target;
		const checked = (e.target as HTMLInputElement).checked;
		setFormData({
			...formData,
			[name]:
				e.target instanceof HTMLInputElement && e.target.type === 'checkbox'
					? checked
					: value,
		});
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files ? Array.from(e.target.files) : [];
		setFormData({
			...formData,
			productImage: files,
		});
		setImagePreviews(files.map((file) => URL.createObjectURL(file)));
	};

	const handleRemoveImage = (index: number) => {
		const newImages = formData.productImage.filter((_, i) => i !== index);
		const newPreviews = imagePreviews.filter((_, i) => i !== index);
		setFormData({
			...formData,
			productImage: newImages,
		});
		setImagePreviews(newPreviews);
	};

	return (
		<div>
			<Typography variant="h6" gutterBottom>
				Create New Product
			</Typography>
			<Grid container spacing={2}>
				<Grid size={{ xs: 12 }}>
					<TextField
						label="Product Name"
						variant="outlined"
						fullWidth
						value={formData.name}
						onChange={(e) => setFormData({ ...formData, name: e.target.value })}
					/>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<TextField
						label="Product Price"
						variant="outlined"
						fullWidth
						value={formData.price}
						onChange={(e) => setFormData({ ...formData, price: e.target.value })}
					/>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<TextField
						label="Product Description"
						variant="outlined"
						fullWidth
						multiline
						rows={4}
						value={formData.description}
						onChange={(e) => setFormData({ ...formData, description: e.target.value })}
					/>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<FormControl fullWidth margin="normal">
						<InputLabel>Category</InputLabel>
						<Select
							name="category"
							value={formData.category}
							onChange={handleChange}
							required
						>
							<MenuItem value="">Select Category</MenuItem>
							{categories?.map((category, index) => (
								<MenuItem key={index} value={category.categoryName}>
									{category.categoryName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<TextField
						label="Quantity"
						name="quantity"
						type="number"
						value={formData.quantity}
						onChange={handleChange}
						required
						fullWidth
						margin="normal"
						inputProps={{ min: '0' }}
					/>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								name="bestSelling"
								checked={formData.bestSelling}
								onChange={handleChange}
							/>
						}
						label="Best Selling"
					/>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								name="trending"
								checked={formData.trending}
								onChange={handleChange}
							/>
						}
						label="Trending"
					/>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<div style={{ marginBottom: '15px' }}>
						<input
							type="file"
							id="productImages"
							name="productImages"
							onChange={handleFileChange}
							required
							accept="image/jpeg, image/png, image/jpg"
							multiple
							style={{ display: 'none' }}
						/>
						<label htmlFor="productImages">
							<Button variant="contained" color="primary" component="span">
								Upload Images
							</Button>
						</label>
						<div>
							{imagePreviews.map((src, index) => (
								<div
									key={index}
									style={{
										display: 'inline-block',
										position: 'relative',
										margin: '5px',
									}}
								>
									<img
										src={src}
										alt={`Preview ${index}`}
										style={{ width: '100px', height: '100px' }}
									/>
									<IconButton
										style={{ position: 'absolute', top: '0', right: '0' }}
										onClick={() => handleRemoveImage(index)}
									>
										<DeleteIcon />
									</IconButton>
								</div>
							))}
						</div>
					</div>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<TextField
						label="Weight"
						name="weight"
						type="number"
						value={formData.weight}
						onChange={handleChange}
						required
						fullWidth
						margin="normal"
					/>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<FormControl fullWidth margin="normal">
						<InputLabel>Weight Unit</InputLabel>
						<Select
							name="weightUnit"
							value={formData.weightUnit}
							onChange={handleChange}
							required
						>
							<MenuItem value="kg">kg</MenuItem>
							<MenuItem value="g">g</MenuItem>
							<MenuItem value="lb">lb</MenuItem>
							<MenuItem value="oz">oz</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				<Grid size={{ xs: 12 }}>
					<Button type="submit" variant="contained" color="primary" fullWidth>
						Create Product
					</Button>
				</Grid>
			</Grid>
		</div>
	);
};

export default CreateProducts;
